import {EventEmitter, Injectable, Output} from '@angular/core';
import {Group} from '../../model/group';
import {Observable} from 'rxjs';
import {IsQueryResponse} from '../register/is-query-response';
import {environment} from '../../environments/environment';
import {SafeDomain} from '../../model/safe-domain';
import {BackendServiceResponse} from '../../model/backend-service-response';
import {User} from '../../model/user';
import {Constants} from '../../common/constants';
import {Set} from '../../model/set';
import {FormMode} from './form-mode.enum';
import {CreateGroupStep} from './create-group-step.enum';
import {MasbitHttpService} from '../../common/http/masbit-http.service';

@Injectable()
export class GroupCreateService {

  private static readonly GROUP_EXISTS_SERVICE_URL = environment.apiUrl.concat(environment.groupExistsServiceUrl);
  private static readonly CREATE_GROUP_SERVICE_URL = environment.apiUrl.concat(environment.createGroupServiceUrl);
  private static readonly CREATE_SAFEDOMAIN_SERVICE_URL = environment.apiUrl.concat(environment.createSafeDomainServiceUrl);
  private static readonly UPDATE_SAFEDOMAIN_SERVICE_URL = environment.apiUrl.concat(environment.updateDomainServiceUrl );
  private static readonly UPDATE_GROUP_SERVICE_URL = environment.apiUrl.concat(environment.updateGroupServiceUrl);

  @Output() formModeChanged = new EventEmitter<FormMode>();
  @Output() createStepChanged = new EventEmitter<CreateGroupStep>();
  @Output() groupChanged = new EventEmitter<Group>();
  @Output() usersToAssignChanged = new EventEmitter<Array<User>>();
  @Output() groupAddedOrModified = new EventEmitter<boolean>();

  showForm(mode : FormMode, createStep : CreateGroupStep, group? : Group, usersToAssign? : Array<User>) {
    this.formModeChanged.next(mode);
    this.createStepChanged.next(createStep);
    if (group)
      this.groupChanged.next(group);
    if (usersToAssign)
      this.usersToAssignChanged.next(usersToAssign);
  }

  groupAddedOrModifiedEmit(result : boolean) {
    this.groupAddedOrModified.next(result);
  }

  constructor(private httpClient : MasbitHttpService) {

  }

  getGroups() : Observable<BackendServiceResponse<string, Array<Group>>> {
    let sortString = 'name ASC';
    return this.httpClient.post<BackendServiceResponse<string, Array<Group>>>(Constants.GET_GROUPS_URL , {'sortString' : sortString});
  }

  groupExists(name : string, id? : number) : Observable<IsQueryResponse> {
    let searchParams = name;
    if (id)
      searchParams = searchParams.concat('/', id.toString());
    return this.httpClient.get<IsQueryResponse>(GroupCreateService.GROUP_EXISTS_SERVICE_URL.concat(searchParams));
  }

  getUsersByDomains(domains : Array<SafeDomain>) : Observable<BackendServiceResponse<string, Array<User>>> {
    let domainsToFind : Array<string> = [];
    domains.forEach((safeDomain : SafeDomain) => {
      domainsToFind.push(safeDomain.name.substr(safeDomain.name.indexOf('@')+1));
    });
    return this.httpClient.post<BackendServiceResponse<string, Array<User>>>(Constants.GET_USER_LIST_SERVICE_URL, {domainFilter : domainsToFind});
  }

  getUsersByGroups(groups : Array<Group>) : Observable<BackendServiceResponse<string, Array<User>>> {
    let groupsToFind : Array<number> = [];
    groups.forEach((group : Group) => {
      groupsToFind.push(group.id);
    });
    return this.httpClient.post<BackendServiceResponse<string, Array<User>>>(Constants.GET_USER_LIST_SERVICE_URL, {groups: groupsToFind});
  }

  getSets(): Observable<BackendServiceResponse<number,Array<Set>>> {
    return this.httpClient.post<BackendServiceResponse<number, Array<Set>>>(Constants.GET_SETS_URL);
  }

  createGroup(group: Group) : Observable<BackendServiceResponse<string, Group>> {
    let groupData : any = {} ;
    groupData.name = group.name;
    groupData.blocked = false;
    groupData.users = group.users;
    groupData.sets = group.sets;
    groupData.safedomains = group.safedomains;
    groupData.sendNotification = group.sendNotification;

    return this.httpClient.post<BackendServiceResponse<string, Group>>(GroupCreateService.CREATE_GROUP_SERVICE_URL, groupData);
  }

  createSafeDomain(safeDomain : SafeDomain) : Observable<BackendServiceResponse<number, SafeDomain>> {
    const safeDomainData = new FormData();
    safeDomainData.append('name', safeDomain.name);
    safeDomainData.append('file', safeDomain.logoFileToUpload);
    return this.httpClient.post<BackendServiceResponse<number, SafeDomain>>(GroupCreateService.CREATE_SAFEDOMAIN_SERVICE_URL, safeDomainData, true);
  }

  updateSafeDomain(domain: SafeDomain) : Observable<BackendServiceResponse<number, SafeDomain>> {
    const safeDomainData = new FormData();
    safeDomainData.append('id', domain.id.toString());
    safeDomainData.append('file', domain.logoFileToUpload);

    return this.httpClient.post<BackendServiceResponse<number, SafeDomain>>(GroupCreateService.UPDATE_SAFEDOMAIN_SERVICE_URL, safeDomainData);
  }

  updateGroup(group : Group) : Observable<BackendServiceResponse<number, Group>> {
    let groupData : any = {};

    groupData.sets = group.sets;
    groupData.safedomains = group.safedomains;
    groupData.name = group.name;
    groupData.users = group.users;
    groupData.id = group.id;
    if (group.sendNotification)
      groupData.sendNotification = 'true';
    else
      groupData.sendNotification = 'false';

    return this.httpClient.post<BackendServiceResponse<number, Group>>(GroupCreateService.UPDATE_GROUP_SERVICE_URL, groupData);
  }
}
